/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useRef, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import toast from "react-hot-toast";
import {MdDelete, MdAdd, MdSearch} from "react-icons/md";
import moderatorService from "../../../services/moderator.service";
import ConfirmModal from "../../../components/confirm-modal";

const Moderators = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const timerRef = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(
    async (reload = false, currentSearch = search) => {
      const currentPage = reload ? 1 : page;
      setLoading(true);
      if (reload) setPage(1);

      try {
        const res = await moderatorService.admin.get({
          page: currentPage,
          withoutLoading: true,
          search: currentSearch,
        });

        // Asegúrate de que el backend ahora devuelve 'moderators' en lugar de 'receivers'
        const rows = res?.moderators?.rows || res?.receivers?.rows || [];
        const count = res?.moderators?.count || res?.receivers?.count || 1;

        setData((prev) => (reload ? rows : [...prev, ...rows]));
        setLastPage(count);
      } catch (error) {
        toast.error(
          typeof error === "string"
            ? error
            : error?.response?.data?.message ||
                error?.message ||
                "Error al cargar moderadores",
        );
      } finally {
        setLoading(false);
        setLoadingInitial(false);
      }
    },
    [page, search],
  );

  useEffect(() => {
    load(true);
  }, []);

  useEffect(() => {
    if (page > 1) load();
  }, [page]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => load(true, val), 1000);
  };

  const handleScroll = (e) => {
    const {scrollTop, clientHeight, scrollHeight} = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight * 1.2 &&
      page < lastPage &&
      !loading
    ) {
      setPage((prev) => prev + 1);
    }
  };

  const openDeleteModal = (item, e) => {
    e.stopPropagation();
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setLoadingDelete(true);
    try {
      await moderatorService.admin.delete({user_id: selectedItem.id});
      toast.success("Se ha eliminado el moderador");
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
      load(true);
    } catch (error) {
      const errorMsg =
        typeof error === "string"
          ? error
          : error?.response?.data?.message ||
            error?.message ||
            "Error al eliminar";
      toast.error(errorMsg);
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <div className="flex flex-col h-full flex-1 min-h-0 px-4 py-6 md:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 shrink-0">
        <div className="relative w-full md:w-1/2 lg:w-1/3">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MdSearch size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar moderadores..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-[#1D3B8B] focus:border-transparent outline-none transition-all"
            value={search}
            onChange={handleSearch}
          />
        </div>

        <button
          onClick={() => navigate("/admin/moderators/create")}
          className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-[#1D3B8B] text-white rounded-xl font-semibold shadow-md hover:bg-orange-600 transition-colors"
        >
          <MdAdd size={20} />
          <span>Nuevo Moderador</span>
        </button>
      </div>

      <div
        className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col flex-1 min-h-0 overflow-hidden"
        ref={listRef}
      >
        <div className="overflow-auto flex-1" onScroll={handleScroll}>
          <table className="w-full text-sm text-left text-gray-600 relative">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50/80 border-b border-gray-100 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-4 font-semibold">Nombre</th>
                <th className="px-6 py-4 font-semibold">Email</th>
                <th className="px-6 py-4 font-semibold">Rol</th>
                <th className="px-6 py-4 font-semibold text-center">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingInitial && data.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-[#1D3B8B] font-medium"
                  >
                    Cargando...
                  </td>
                </tr>
              )}

              {!loadingInitial && data.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    No se han encontrado registros
                  </td>
                </tr>
              )}

              {data.map((item, index) => (
                <tr
                  key={item.id || index}
                  className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                  onClick={() =>
                    navigate("/admin/moderators/create", {state: {item}})
                  }
                >
                  <td className="px-6 py-4 font-bold text-[#1D3B8B] whitespace-nowrap">
                    {`${item.name} ${item.lastname || ""}`}
                  </td>
                  <td className="px-6 py-4">{item.email}</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">
                      {item.level?.name || "Desconocido"}
                    </span>
                  </td>
                  <td className="px-6 py-4 flex justify-center">
                    <button
                      type="button"
                      onClick={(e) => openDeleteModal(item, e)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar moderador"
                    >
                      <MdDelete size={22} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {loading && lastPage > 1 && (
            <div className="px-6 py-4 text-center text-[#1D3B8B] font-medium border-t border-gray-50 bg-gray-50/50">
              Cargando más registros...
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="¿Desea eliminar el moderador?"
        message="Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
        loading={loadingDelete}
        onConfirm={handleConfirmDelete}
        onClose={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default Moderators;
